import React, { ChangeEvent, useEffect, useState } from 'react'
import { useFilters } from '../../hooks'
import { Period, Theme } from '../../types'

import ThemeClassifierModal from './ThemeClassifierModal'
import MethodologyModal from './MethodologyModal'
import * as Styled from './Filters.styled'

const METHODOLOGY_MODAL_NAME = 'data-methodology-modal'

export type FilterInputProps = {
  data?: { period?: Period; theme?: Theme[]; category?: Theme[]; region?: Theme[] }
  isFetching: boolean
  formValues: Record<string, string>
  availableDateRange: { min: Date | undefined; max: Date | undefined }
  isCheckboxChecked: (name: string, value: string) => boolean
  handleSelectChange: (value: string | number, label: string) => void
  handleCheckboxChange: (checked: boolean, e: ChangeEvent<HTMLInputElement>) => void
  handleDateChange: (date: { min: Date; max: Date }) => void
  handleOpenThemeClassifierModal: () => void
}

export type BaseFilterProps = {
  isFetching: boolean
  themeClassifierModalData?: { title: string; description: string }[]
  methodologyModalData: {
    period: string | React.ReactNode
    placement?: string | React.ReactNode
    analytics?: string | React.ReactNode
    description: string | React.ReactNode
  }
  data?: { period?: Period; theme?: Theme[] }
  className?: string
  fetchData: () => void
  isNewGrid?: boolean
  FilterInputs: React.ElementType<FilterInputProps>
}

export type FiltersProps = {
  isFetching: boolean
  data?: { period?: Period; theme?: Theme[]; region?: Theme[]; category?: Theme[] }
  fetchData: () => void
}

const Filters: React.FC<BaseFilterProps> = ({
  className,
  data,
  isFetching,
  methodologyModalData,
  themeClassifierModalData,
  fetchData,
  FilterInputs,
  isNewGrid
}) => {
  const { formValues, onChangeQuery } = useFilters(fetchData)
  const [isThemeClassifierModalOpen, setIsClassifierModalOpen] = useState(false)
  const [isMethodologyModalOpen, setIsMethodologyModalOpen] = useState(
    () => location.hash === `#${METHODOLOGY_MODAL_NAME}`
  )
  useEffect(() => {
    window.onhashchange = () => {
      if (location.hash === `#${METHODOLOGY_MODAL_NAME}`) {
        setIsMethodologyModalOpen(true)
      }
    }

    return () => {
      window.onhashchange = null
    }
  }, [])

  const handleSelectChange = (value: string | number, label: string) => {
    onChangeQuery({ name: label, value: value.toString() })
  }

  const handleCheckboxChange = (checked: boolean, e: ChangeEvent<HTMLInputElement>) => {
    const newValue = checked
      ? [...formValues[e.target.name].split(','), e.target.value]
      : formValues[e.target.name].split(',').filter(val => val !== e.target.value)
    onChangeQuery({ name: e.target.name, value: newValue.join(',') })
  }

  const handleDateChange = ({ min, max }: { min: Date; max: Date }) => {
    onChangeQuery(
      { name: 'min', value: (+min).toString() },
      { name: 'max', value: (+max).toString() }
    )
  }

  const isCheckboxChecked = (name: string, value: string) => formValues?.[name]?.includes(value)

  const handleCloseMethodologyModal = () => {
    setIsMethodologyModalOpen(false)
    history.replaceState(
      null,
      document.title,
      location.origin + location.pathname + location.search
    )
  }

  const handleCloseThemeClassifierModal = () => setIsClassifierModalOpen(false)

  const handleOpenThemeClassifierModal = () => setIsClassifierModalOpen(true)

  const availableDateRange = data?.period
    ? { min: new Date(data.period.min), max: new Date(data.period.max) }
    : { min: undefined, max: undefined }

  return (
    <>
      <Styled.Container
        className={`js-sticky ${className}`}
        data-sticky-class="filters--sticky"
        data-margin-top={100}
        data-sticky-for={1279}
        data-sticky-wrap
        isNewGrid={!!isNewGrid}
      >
        <FilterInputs
          data={data}
          formValues={formValues}
          isFetching={isFetching}
          availableDateRange={availableDateRange}
          isCheckboxChecked={isCheckboxChecked}
          handleSelectChange={handleSelectChange}
          handleCheckboxChange={handleCheckboxChange}
          handleDateChange={handleDateChange}
          handleOpenThemeClassifierModal={handleOpenThemeClassifierModal}
        />
      </Styled.Container>
      {themeClassifierModalData && (
        <ThemeClassifierModal
          data={themeClassifierModalData}
          isOpen={isThemeClassifierModalOpen}
          onClose={handleCloseThemeClassifierModal}
        />
      )}
      <MethodologyModal
        data={methodologyModalData}
        isOpen={isMethodologyModalOpen}
        onClose={handleCloseMethodologyModal}
      />
    </>
  )
}

export default Filters
